import { useCallback } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { commercialCyclesApi, CommercialCycle } from "@/lib/api";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { CYCLE_QUERY_KEYS } from "./constants";

function cycleTransitionErrorMessage(error: AxiosError<{ message?: string }>, fallback: string): string {
  return error.response?.data?.message || fallback;
}

export function useCommercialCycles() {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const {
    data: cycles = [],
    isLoading,
  } = useQuery<CommercialCycle[]>({
    queryKey: CYCLE_QUERY_KEYS.list,
    queryFn: async () => {
      const response = await commercialCyclesApi.list();
      return response.data || [];
    },
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: CYCLE_QUERY_KEYS.list }),
    [queryClient],
  );

  const publishMutation = useMutation({
    mutationFn: (year: number) => commercialCyclesApi.publish(year),
    onSuccess: () => {
      toast.success("Ciclo publicado com sucesso!");
      invalidate();
    },
    onError: (error: AxiosError<{ message?: string }>) =>
      toast.error(cycleTransitionErrorMessage(error, "Erro ao publicar o ciclo.")),
  });

  const openMutation = useMutation({
    mutationFn: (year: number) => commercialCyclesApi.open(year),
    onSuccess: () => {
      toast.success("Ciclo aberto com sucesso!");
      invalidate();
    },
    onError: (error: AxiosError<{ message?: string }>) =>
      toast.error(cycleTransitionErrorMessage(error, "Erro ao abrir o ciclo.")),
  });

  const startClosingMutation = useMutation({
    mutationFn: (year: number) => commercialCyclesApi.startClosing(year),
    onSuccess: () => {
      toast.success("Fechamento do ciclo iniciado com sucesso!");
      invalidate();
    },
    onError: (error: AxiosError<{ message?: string }>) =>
      toast.error(cycleTransitionErrorMessage(error, "Erro ao iniciar o fechamento do ciclo.")),
  });

  const closeMutation = useMutation({
    mutationFn: (year: number) => commercialCyclesApi.close(year),
    onSuccess: () => {
      toast.success("Ciclo fechado com sucesso!");
      invalidate();
    },
    onError: (error: AxiosError<{ message?: string }>) =>
      toast.error(cycleTransitionErrorMessage(error, "Erro ao fechar o ciclo.")),
  });

  const handlePublish = useCallback(
    async (year: number) => {
      const confirmed = await confirm({
        title: `Publicar ciclo ${year}?`,
        description:
          "A partir da publicação, o Portal do Cliente passará a exigir a escolha do ciclo sempre que houver mais de um ciclo publicado.",
        confirmLabel: "Publicar",
      });
      if (confirmed) publishMutation.mutate(year);
    },
    [confirm, publishMutation],
  );

  const handleOpen = useCallback(
    async (year: number) => {
      const confirmed = await confirm({
        title: `Abrir ciclo ${year}?`,
        description:
          "A partir da abertura, o ciclo passará a aceitar criação e edição de pedidos.",
        confirmLabel: "Abrir",
      });
      if (confirmed) openMutation.mutate(year);
    },
    [confirm, openMutation],
  );

  const handleStartClosing = useCallback(
    async (year: number) => {
      const confirmed = await confirm({
        title: `Iniciar fechamento do ciclo ${year}?`,
        description:
          "Novas operações no ciclo serão bloqueadas em preparação para o fechamento. Nenhum dado é apagado.",
        confirmLabel: "Iniciar fechamento",
        variant: "destructive",
      });
      if (confirmed) startClosingMutation.mutate(year);
    },
    [confirm, startClosingMutation],
  );

  const handleClose = useCallback(
    async (year: number) => {
      const confirmed = await confirm({
        title: `Fechar ciclo ${year}?`,
        description:
          "O ciclo deixa de ser considerado na resolução automática do backend. Nenhum dado é apagado.",
        confirmLabel: "Fechar",
        variant: "destructive",
      });
      if (confirmed) closeMutation.mutate(year);
    },
    [confirm, closeMutation],
  );

  return {
    cycles,
    isLoading,
    invalidate,
    publishMutation,
    openMutation,
    startClosingMutation,
    closeMutation,
    handlePublish,
    handleOpen,
    handleStartClosing,
    handleClose,
    ConfirmDialog,
  };
}
